import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import DoctorNavbar from '../components/DoctorNavbar';
import { toast } from 'react-toastify';
import { clearDoctorSessionLocal, setDoctorSession } from '../utils/auth';
import { useIntervalCallback, DOCTOR_POLL_MS } from '../hooks/useDoctorLiveRefresh';

function DoctorNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [sessionOk, setSessionOk] = useState(false);

  useEffect(() => {
    checkSession();
  }, []);

  useIntervalCallback(() => fetchNotifications(false), DOCTOR_POLL_MS, sessionOk);

  const checkSession = async () => {
    try {
      const res = await api.get('doctor/session-check/');
      if (res.data.authenticated) {
        setDoctorSession(res.data.doctor_id);
        setSessionOk(true);
        fetchNotifications();
      } else {
        setSessionOk(false);
        clearDoctorSessionLocal();
        navigate('/doctor/login', { replace: true });
      }
    } catch (err) {
      console.error('Session check failed:', err);
      setSessionOk(false);
      clearDoctorSessionLocal();
      navigate('/doctor/login', { replace: true });
    }
  };

  const fetchNotifications = async (showLoading = true) => {
    try {
      const res = await api.get('notification/doctor/');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`notification/doctor/mark-read/${notificationId}/`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('notification/doctor/mark-all-read/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`notification/doctor/delete/${notificationId}/`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  const handleClearAllClick = () => {
    setShowClearConfirm(true);
  };

  const clearAllNotifications = async () => {
    try {
      await api.delete('notification/doctor/clear-all/');
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared successfully!', {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Error clearing notifications:", err);
      toast.error('Failed to clear notifications', {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setShowClearConfirm(false);
    }
  };

  const getNotificationStyle = (type) => {
    const styles = {
      'APPOINTMENT_BOOKED': {
        icon: 'bi-calendar-plus-fill',
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        bgLight: 'rgba(59, 130, 246, 0.1)',
        color: '#2563eb'
      },
      'APPOINTMENT_CANCELLED': {
        icon: 'bi-calendar-x-fill',
        gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        bgLight: 'rgba(239, 68, 68, 0.1)',
        color: '#dc2626'
      },
      'APPOINTMENT_RESCHEDULED': {
        icon: 'bi-calendar-event-fill',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        bgLight: 'rgba(245, 158, 11, 0.1)',
        color: '#d97706'
      },
      'PAYMENT_SUCCESS': {
        icon: 'bi-credit-card-2-front-fill',
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        bgLight: 'rgba(16, 185, 129, 0.1)',
        color: '#059669'
      },
      'APPOINTMENT_REMINDER': {
        icon: 'bi-alarm-fill',
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        bgLight: 'rgba(139, 92, 246, 0.1)',
        color: '#7c3aed'
      }
    };
    return styles[type] || {
      icon: 'bi-bell-fill',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      bgLight: 'rgba(6, 182, 212, 0.1)',
      color: '#0891b2'
    };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  const styles = {
    pageWrapper: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      minHeight: '100vh'
    },
    headerCard: {
      background: 'transparent',
      borderRadius: '24px',
      padding: '32px 0',
      marginBottom: '24px'
    },
    backButton: {
      width: '48px',
      height: '48px',
      borderRadius: '14px',
      background: 'rgba(14, 165, 233, 0.1)',
      border: '1px solid rgba(14, 165, 233, 0.2)',
      color: '#0ea5e9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    unreadBadge: {
      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      padding: '6px 14px',
      borderRadius: '30px',
      fontSize: '13px',
      fontWeight: '600',
      color: 'white',
      boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)'
    },
    actionButton: {
      padding: '12px 24px',
      borderRadius: '12px',
      border: '1px solid rgba(14, 165, 233, 0.3)',
      fontWeight: '600',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    filterContainer: {
      background: 'white',
      borderRadius: '20px',
      padding: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      marginBottom: '24px'
    },
    filterButton: (isActive) => ({
      flex: 1,
      padding: '14px 20px',
      borderRadius: '14px',
      border: 'none',
      background: isActive ? 'linear-gradient(135deg, #0056b3 0%, #004494 100%)' : 'transparent',
      color: isActive ? 'white' : '#64748b',
      fontWeight: '600',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }),
    notificationCard: (isRead) => ({
      background: isRead ? 'white' : 'linear-gradient(135deg, #fefefe 0%, #f0f9ff 100%)',
      borderRadius: '20px',
      padding: '24px',
      marginBottom: '16px',
      boxShadow: isRead ? '0 2px 12px rgba(0, 0, 0, 0.04)' : '0 8px 30px rgba(14, 165, 233, 0.15)',
      border: isRead ? '1px solid #e2e8f0' : '1px solid rgba(14, 165, 233, 0.2)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden'
    }),
    iconWrapper: (gradient) => ({
      width: '56px',
      height: '56px',
      borderRadius: '16px',
      background: gradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
      flexShrink: 0
    }),
    newBadge: {
      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    actionButtonSmall: (variant) => {
      const variants = {
        primary: {
          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
          color: 'white',
          boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)'
        },
        secondary: {
          background: '#f1f5f9',
          color: '#475569',
          boxShadow: 'none'
        },
        danger: {
          background: 'rgba(239, 68, 68, 0.1)',
          color: '#dc2626',
          boxShadow: 'none'
        }
      };
      return {
        padding: '10px 18px',
        borderRadius: '10px',
        border: 'none',
        fontWeight: '600',
        fontSize: '13px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        ...variants[variant]
      };
    },
    emptyState: {
      background: 'white',
      borderRadius: '24px',
      padding: '60px 40px',
      textAlign: 'center',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
    },
    emptyIcon: {
      width: '120px',
      height: '120px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
      fontSize: '48px',
      color: '#94a3b8'
    },
    loadingWrapper: {
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    },
    spinner: {
      width: '60px',
      height: '60px',
      border: '4px solid #e2e8f0',
      borderTop: '4px solid #0ea5e9',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    unreadIndicator: {
      position: 'absolute',
      left: '0',
      top: '0',
      bottom: '0',
      width: '4px',
      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      borderRadius: '0 4px 4px 0'
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingWrapper}>
        <div className="text-center">
          <div style={styles.spinner}></div>
          <p className="mt-3 text-muted fw-medium">Loading notifications...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <DoctorNavbar />
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .notification-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(14, 165, 233, 0.2) !important;
        }
        .action-btn:hover {
          transform: translateY(-2px);
        }
        .filter-btn:hover {
          background: #f1f5f9;
        }
        .back-btn:hover {
          background: rgba(14, 165, 233, 0.2) !important;
          transform: scale(1.05);
        }
      `}</style>

      <div className="container py-4">
        {/* Header Card */}
        <div style={styles.headerCard}>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <button
                className="back-btn"
                style={styles.backButton}
                onClick={() => navigate('/doctor/dashboard')}
              >
                <i className="bi bi-arrow-left fs-5"></i>
              </button>
              <div>
                <div className="d-flex align-items-center gap-3 mb-1">
                  <h2 className="fw-bold mb-0 text-primary">Notifications</h2>
                  {unreadCount > 0 && (
                    <span style={styles.unreadBadge}>
                      {unreadCount} new
                    </span>
                  )}
                </div>
                <p className="mb-0" style={{ color: '#64748b' }}>
                  Stay updated with appointment requests and activities
                </p>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {unreadCount > 0 && (
                <button
                  className="action-btn"
                  style={{
                    ...styles.actionButton,
                    background: 'rgba(14, 165, 233, 0.1)',
                    color: '#0ea5e9'
                  }}
                  onClick={markAllNotificationsRead}
                >
                  <i className="bi bi-check-all"></i>
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  className="action-btn"
                  style={{
                    ...styles.actionButton,
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#dc2626',
                    border: '1px solid rgba(239, 68, 68, 0.3)'
                  }}
                  onClick={handleClearAllClick}
                >
                  <i className="bi bi-trash3"></i>
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div style={styles.filterContainer}>
          <div className="d-flex gap-2">
            <button
              className="filter-btn"
              style={styles.filterButton(filter === 'all')}
              onClick={() => setFilter('all')}
            >
              <i className="bi bi-grid-fill"></i>
              All
              <span style={{
                background: filter === 'all' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                padding: '2px 10px',
                borderRadius: '20px',
                fontSize: '12px'
              }}>
                {notifications.length}
              </span>
            </button>
            <button
              className="filter-btn"
              style={styles.filterButton(filter === 'unread')}
              onClick={() => setFilter('unread')}
            >
              <i className="bi bi-envelope-fill"></i>
              Unread
              <span style={{
                background: filter === 'unread' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                padding: '2px 10px',
                borderRadius: '20px',
                fontSize: '12px'
              }}>
                {unreadCount}
              </span>
            </button>
            <button
              className="filter-btn"
              style={styles.filterButton(filter === 'read')}
              onClick={() => setFilter('read')}
            >
              <i className="bi bi-envelope-open-fill"></i>
              Read
              <span style={{
                background: filter === 'read' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                padding: '2px 10px',
                borderRadius: '20px',
                fontSize: '12px'
              }}>
                {notifications.length - unreadCount}
              </span>
            </button>
          </div>
        </div>

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <i className="bi bi-bell-slash"></i>
            </div>
            <h4 className="fw-bold text-dark mb-2">No notifications</h4>
            <p className="text-muted mb-0" style={{ maxWidth: '300px', margin: '0 auto' }}>
              {filter === 'unread' ? 'You have read all your notifications! Great job staying on top of things.' :
                filter === 'read' ? 'No read notifications yet. Your read notifications will appear here.' :
                  'You don\'t have any notifications yet. We\'ll notify you when something happens.'}
            </p>
          </div>
        ) : (
          <div>
            {filteredNotifications.map((notification, index) => {
              const notifStyle = getNotificationStyle(notification.notification_type);
              return (
                <div
                  key={notification.id}
                  className="notification-card"
                  style={{
                    ...styles.notificationCard(!notification.is_read),
                    animation: `slideIn 0.3s ease forwards`,
                    animationDelay: `${index * 0.05}s`,
                    opacity: 0
                  }}
                >
                  {!notification.is_read && <div style={styles.unreadIndicator}></div>}

                  <div className="d-flex gap-3">
                    <div style={styles.iconWrapper(notifStyle.gradient)}>
                      <i className={`bi ${notifStyle.icon} text-white fs-4`}></i>
                    </div>

                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <h6 className={`mb-0 ${!notification.is_read ? 'fw-bold' : 'fw-semibold'}`}
                            style={{ color: '#1e293b', fontSize: '16px' }}>
                            {notification.title}
                          </h6>
                          {!notification.is_read && (
                            <span style={styles.newBadge}>New</span>
                          )}
                        </div>
                        <button
                          className="action-btn"
                          style={styles.actionButtonSmall('danger')}
                          onClick={() => deleteNotification(notification.id)}
                        >
                          <i className="bi bi-trash3"></i>
                        </button>
                      </div>

                      <p style={{
                        color: '#64748b',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        marginBottom: '16px'
                      }}>
                        {notification.message}
                      </p>

                      <div className="d-flex gap-2 flex-wrap">
                        {!notification.is_read && (
                          <button
                            className="action-btn"
                            style={styles.actionButtonSmall('secondary')}
                            onClick={() => markNotificationRead(notification.id)}
                          >
                            <i className="bi bi-check2"></i>
                            Mark as read
                          </button>
                        )}
                        <span style={{
                          color: '#94a3b8',
                          fontSize: '13px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <i className="bi bi-clock"></i>
                          {formatDate(notification.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CLEAR ALL CONFIRMATION MODAL */}
      {showClearConfirm && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>Confirm Clear All
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowClearConfirm(false)}></button>
              </div>
              <div className="modal-body p-4">
                <p className="mb-0">
                  Are you sure you want to clear all notifications? This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => setShowClearConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning rounded-3 fw-bold"
                  onClick={clearAllNotifications}
                >
                  <i className="bi bi-trash me-2"></i>Clear All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DoctorNotifications;
